import { useState, useMemo } from "react";
import { useAccount } from "wagmi";
import {
  Search,
  Filter,
  Sprout,
  Zap,
  TrendingUp,
  Wallet,
  Loader2,
} from "lucide-react";
import { cn } from "../lib/utils";
import { FarmCard, type Farm } from "../components/farms/FarmCard";
import { useFarms } from "../hooks/useFarms";

/* ================================================================== */
/* TYPES                                                               */
/* ================================================================== */

type FarmTab = "live" | "finished";
type SortKey = "hot" | "apr" | "earned" | "liquidity";

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: "hot", label: "Hot" },
  { value: "apr", label: "APR" },
  { value: "earned", label: "Earned" },
  { value: "liquidity", label: "Liquidity" },
];

/* ================================================================== */
/* HELPER: parse liquidity string to number                            */
/* ================================================================== */

function parseLiquidity(liq: string): number {
  const stripped = liq.replace(/[^0-9.]/g, "");
  return parseFloat(stripped) || 0;
}

/* ================================================================== */
/* STATS BANNER                                                        */
/* ================================================================== */

function StatsBanner({ farms, isLoading }: { farms: Farm[]; isLoading: boolean }) {
  const activeFarms = farms.filter((f) => f.isActive).length;

  return (
    <div className="glass-card p-6 sm:p-8 mb-8">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
        <div className="text-center sm:text-left">
          <div className="flex items-center justify-center sm:justify-start gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-[#F7931A]/10 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-primary" />
            </div>
            <span className="text-sm text-text-tertiary font-medium">
              Total Pools
            </span>
          </div>
          <p className="neon-text-orange font-display text-2xl sm:text-3xl font-bold">
            {isLoading ? (
              <Loader2 className="w-6 h-6 animate-spin inline-block" />
            ) : (
              farms.length
            )}
          </p>
        </div>

        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-[#8B2FC9]/10 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-secondary" />
            </div>
            <span className="text-sm text-text-tertiary font-medium">
              JASMY Earned
            </span>
          </div>
          <p className="neon-text-orange font-display text-2xl sm:text-3xl font-bold">
            0.00
          </p>
        </div>

        <div className="text-center sm:text-right">
          <div className="flex items-center justify-center sm:justify-end gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-[#39FF14]/10 flex items-center justify-center">
              <Sprout className="w-4 h-4 text-success" />
            </div>
            <span className="text-sm text-text-tertiary font-medium">
              Active Pools
            </span>
          </div>
          <p className="neon-text-orange font-display text-2xl sm:text-3xl font-bold">
            {isLoading ? (
              <Loader2 className="w-6 h-6 animate-spin inline-block" />
            ) : (
              activeFarms
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/* FARMS PAGE                                                          */
/* ================================================================== */

export function FarmsPage() {
  const { isConnected } = useAccount();
  const { farms: liveFarms, isLoading } = useFarms();
  const [activeTab, setActiveTab] = useState<FarmTab>("live");
  const [stakedOnly, setStakedOnly] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>("hot");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);

  /* ---- Filter and sort ---- */
  const filteredFarms = useMemo(() => {
    let farms = [...liveFarms];

    /* Tab filter */
    if (activeTab === "live") {
      farms = farms.filter((f) => f.isActive);
    } else {
      farms = farms.filter((f) => !f.isActive);
    }

    /* Staked only */
    if (stakedOnly) {
      farms = farms.filter((f) => parseFloat(f.staked) > 0);
    }

    /* Search */
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      farms = farms.filter(
        (f) =>
          f.pair.toLowerCase().includes(q) ||
          f.token0Symbol.toLowerCase().includes(q) ||
          f.token1Symbol.toLowerCase().includes(q)
      );
    }

    /* Sort */
    switch (sortBy) {
      case "apr":
        farms.sort((a, b) => b.apr - a.apr);
        break;
      case "earned":
        farms.sort(
          (a, b) => parseFloat(b.earned) - parseFloat(a.earned)
        );
        break;
      case "liquidity":
        farms.sort(
          (a, b) => parseLiquidity(b.liquidity) - parseLiquidity(a.liquidity)
        );
        break;
      case "hot":
      default:
        farms.sort((a, b) => parseInt(b.multiplier) - parseInt(a.multiplier));
        break;
    }

    return farms;
  }, [liveFarms, activeTab, stakedOnly, sortBy, searchQuery]);

  return (
    <div className="relative min-h-[calc(100vh-4rem)]">
      {/* Background orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-60 -right-60 w-[800px] h-[800px] rounded-full bg-primary/[0.03] blur-[150px]" />
        <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-secondary/[0.04] blur-[120px]" />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full bg-[#39FF14]/[0.02] blur-[130px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-20">
        {/* ---- Page Header ---- */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 text-sm text-primary font-medium mb-4">
            <Sprout className="w-3.5 h-3.5" />
            Yield Farming
          </div>
          <h1 className="neon-text-orange font-display text-4xl sm:text-5xl font-bold mb-3 animate-neon-pulse">
            Yield Farms
          </h1>
          <p className="text-text-secondary text-base sm:text-lg max-w-lg mx-auto">
            Stake LP tokens to earn JASMY rewards
          </p>
        </div>

        {/* ---- Stats Banner ---- */}
        <StatsBanner farms={liveFarms} isLoading={isLoading} />

        {/* ---- Filter Bar ---- */}
        <div className="glass-card p-4 mb-8">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-4">
            {/* Tabs */}
            <div className="flex items-center gap-1 bg-white/[0.03] rounded-button p-1">
              <button
                onClick={() => setActiveTab("live")}
                className={cn(
                  "px-4 py-2 rounded-[10px] text-sm font-semibold transition-all duration-200",
                  activeTab === "live"
                    ? "bg-primary/15 text-primary shadow-[0_0_12px_rgba(247,147,26,0.15)]"
                    : "text-text-tertiary hover:text-text-secondary"
                )}
              >
                <span className="flex items-center gap-1.5">
                  <Zap className="w-3.5 h-3.5" />
                  Live
                </span>
              </button>
              <button
                onClick={() => setActiveTab("finished")}
                className={cn(
                  "px-4 py-2 rounded-[10px] text-sm font-semibold transition-all duration-200",
                  activeTab === "finished"
                    ? "bg-white/10 text-text-primary"
                    : "text-text-tertiary hover:text-text-secondary"
                )}
              >
                Finished
              </button>
            </div>

            {/* Staked Only Toggle */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setStakedOnly(!stakedOnly)}
                className="relative flex items-center"
                role="switch"
                aria-checked={stakedOnly}
              >
                <div
                  className={cn(
                    "w-11 h-6 rounded-full transition-all duration-200",
                    stakedOnly
                      ? "bg-primary/30 border border-primary/50 shadow-[0_0_10px_rgba(247,147,26,0.2)]"
                      : "bg-white/[0.06] border border-white/10"
                  )}
                >
                  <div
                    className={cn(
                      "w-4 h-4 rounded-full absolute top-1 transition-all duration-200",
                      stakedOnly
                        ? "left-6 bg-primary shadow-[0_0_8px_rgba(247,147,26,0.5)]"
                        : "left-1 bg-white/30"
                    )}
                  />
                </div>
              </button>
              <span className="text-sm text-text-secondary font-medium">
                Staked Only
              </span>
            </div>

            {/* Spacer */}
            <div className="hidden lg:block flex-1" />

            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                onBlur={() => setTimeout(() => setSortDropdownOpen(false), 150)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-button bg-white/[0.03] border border-white/[0.08] hover:border-white/[0.18] transition-all text-sm text-text-secondary font-medium min-w-[140px]"
              >
                <Filter className="w-3.5 h-3.5 text-text-tertiary" />
                Sort: {SORT_OPTIONS.find((o) => o.value === sortBy)?.label}
              </button>
              {sortDropdownOpen && (
                <div className="absolute top-full mt-1 right-0 z-20 min-w-[160px] rounded-card bg-[#0f1024] border border-white/[0.1] shadow-[0_8px_32px_rgba(0,0,0,0.5)] py-1 animate-fade-in">
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value);
                        setSortDropdownOpen(false);
                      }}
                      className={cn(
                        "w-full text-left px-4 py-2.5 text-sm transition-colors",
                        sortBy === option.value
                          ? "text-primary bg-primary/10"
                          : "text-text-secondary hover:text-text-primary hover:bg-white/[0.04]"
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary pointer-events-none" />
              <input
                type="text"
                placeholder="Search farms..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="input-field pl-10 pr-4 py-2.5 text-sm w-full lg:w-[220px]"
              />
            </div>
          </div>
        </div>

        {/* ---- Loading State ---- */}
        {isLoading && liveFarms.length === 0 ? (
          <div className="glass-card p-16 text-center">
            <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
            <p className="font-display text-xl font-semibold text-text-secondary mb-2">
              Loading Pools
            </p>
            <p className="text-sm text-text-tertiary max-w-sm mx-auto">
              Fetching liquidity pool data from JasmyChain...
            </p>
          </div>
        ) : filteredFarms.length > 0 ? (
          /* ---- Farm Cards Grid ---- */
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {filteredFarms.map((farm, i) => (
              <FarmCard key={farm.id} farm={farm} index={i} />
            ))}
          </div>
        ) : (
          <div className="glass-card p-16 text-center">
            <Sprout className="w-12 h-12 text-text-tertiary mx-auto mb-4" />
            <p className="font-display text-xl font-semibold text-text-secondary mb-2">
              No Farms Found
            </p>
            <p className="text-sm text-text-tertiary max-w-sm mx-auto">
              {stakedOnly
                ? "You are not staking in any farms yet. Disable the \"Staked Only\" filter to see all available farms."
                : activeTab === "finished"
                  ? "There are no finished farms at the moment."
                  : searchQuery.trim()
                    ? "No farms match your search. Try a different keyword."
                    : "No liquidity pools have been created yet."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
